/**
 * Form values and rules of the model dialog, plus the list helpers of the
 * models screen.
 */
import { DMS_PRODUCTLINE } from '@/shared/constants';
import { getImage } from '@/shared/lib/image';
import { sortByKey } from '@/shared/lib/sort';
import { z } from 'zod';
import { namedItemSchema } from '../named-items/api';
import type { SewingPattern } from '../sewing-patterns/api';
import type { DmsItem } from '../api';
import { displayAvailability, textKey, type ListSort } from '../list';

/** `data` of a model item; the fields the iPad app adds are kept (round trip). */
export const modelDataSchema = z
  .object({
    productLine: z.string().nullish(),
    model: z.string().nullish(),
    modelStyleCodePart: z.string().nullish(),
    fit: z.string().nullish(),
    sizes: z.string().nullish(),
    description: z.string().nullish(),
    provalineas: z.string().nullish(),
    jacketModel: z.string().nullish(),
    pantsModel: z.string().nullish(),
    availablePermanent: z.boolean().nullish(),
    availableVillaDior: z.boolean().nullish(),
    eveningOffer: z.boolean().nullish(),
    /** Names kept by the API for the front office; the dialog edits the objects. */
    sewingPatterns: z.array(z.string()).nullish(),
    sewingPatternObjects: z.array(namedItemSchema).nullish(),
    lightPicture: z.string().nullish(),
    darkPicture: z.string().nullish(),
    basePicture: z.string().nullish(),
    options: z.array(z.unknown()).nullish(),
    compatibilities: z.array(z.unknown()).nullish(),
    incompatibilities: z.array(z.unknown()).nullish(),
  })
  .loose();
export type ModelData = z.infer<typeof modelDataSchema>;

export type DmsModel = DmsItem<ModelData>;

export const MODEL_PICTURE_TYPES = ['light', 'dark', 'base'] as const;
export type ModelPictureType = (typeof MODEL_PICTURE_TYPES)[number];

/** Suffix of the picture file name on the bucket, after the model name. */
export const MODEL_PICTURE_SUFFIX: Record<ModelPictureType, string> = {
  light: '_WHITE',
  dark: '_BLACK',
  base: '_BASE',
};

export interface ModelFormValues {
  productLine: string;
  model: string;
  modelStyleCodePart: string;
  fit: string;
  sizes: string;
  description: string;
  provalineas: string;
  jacketModel: string;
  pantsModel: string;
  availablePermanent: boolean;
  availableVillaDior: boolean;
  eveningOffer: boolean;
  sewingPatternObjects: SewingPattern[];
}

export type ModelFormErrors = Partial<Record<keyof ModelFormValues, string>>;

export const modelFormValues = (
  item: DmsModel | null,
  productLine: string,
): ModelFormValues => ({
  productLine: item?.data.productLine ?? productLine,
  model: item?.data.model ?? '',
  modelStyleCodePart: item?.data.modelStyleCodePart ?? '',
  fit: item?.data.fit ?? '',
  sizes: item?.data.sizes ?? '',
  description: item?.data.description ?? '',
  provalineas: item?.data.provalineas ?? '',
  jacketModel: item?.data.jacketModel ?? '',
  pantsModel: item?.data.pantsModel ?? '',
  availablePermanent: !!item?.data.availablePermanent,
  availableVillaDior: !!item?.data.availableVillaDior,
  eveningOffer: !!item?.data.eveningOffer,
  sewingPatternObjects: item?.data.sewingPatternObjects ?? [],
});

const MODEL_CODE_PATTERN = /^[a-zA-Z0-9]{2}$/;

/**
 * Product line, model name, code (two alphanumerics) and
 * sizes are required; the jacket and pants models only once the suit line has
 * been picked in the dialog (the validators are set by the change handler,
 * never on open, so an edited suit keeps them optional: see migration.md).
 */
export const validateModelForm = (
  values: ModelFormValues,
  suitRulesApply: boolean,
): ModelFormErrors => {
  const errors: ModelFormErrors = {};
  if (!values.productLine) errors.productLine = 'Wrong product line';
  if (!values.model.trim()) errors.model = 'Wrong model name';
  if (!MODEL_CODE_PATTERN.test(values.modelStyleCodePart))
    errors.modelStyleCodePart = 'Wrong model code';
  if (!values.sizes.trim()) errors.sizes = 'Wrong sizes';
  if (suitRulesApply) {
    if (!values.jacketModel.trim()) errors.jacketModel = 'Wrong jacket model';
    if (!values.pantsModel.trim()) errors.pantsModel = 'Wrong pants model';
  }
  return errors;
};

/** The form, the relations kept from the item, and the picture stamps. */
export const formToModelData = (
  values: ModelFormValues,
  item: DmsModel | null,
  now: number,
): ModelData => ({
  ...values,
  fit: values.fit || null,
  options: item ? item.data.options : [],
  compatibilities: item ? item.data.compatibilities : [],
  incompatibilities: item ? item.data.incompatibilities : [],
  lightPictureUpdatedAt: now,
  darkPictureUpdatedAt: now,
  basePictureUpdatedAt: now,
});

export const MODEL_SORT_FIELDS = [
  'model',
  'modelStyleCodePart',
  'fit',
  'displayAvailability',
] as const;
export type ModelSortField = (typeof MODEL_SORT_FIELDS)[number];

export type ModelSort = ListSort<ModelSortField>;

/** The models of the product line, sorted by lower-cased text. */
export const displayedModels = (
  items: readonly DmsModel[],
  productLine: string,
  sort: ModelSort,
): DmsModel[] =>
  sortByKey(
    items.filter((item) => item.data.productLine === productLine),
    (item) =>
      textKey(
        sort.by === 'displayAvailability'
          ? displayAvailability(item.data)
          : item.data[sort.by],
      ),
    sort.reverse,
  );

/** The picture check mark: light and dark pictures, plus the base one for shirts. */
export const hasAllPictures = (data: ModelData, productLine: string): boolean =>
  !!data.lightPicture &&
  !!data.darkPicture &&
  (productLine !== DMS_PRODUCTLINE.SHIRT || !!data.basePicture);

/** Signed URL of a picture stored on the DMS bucket. */
export const modelPictureUrl = (
  item: DmsModel | null,
  type: ModelPictureType,
): string | null => {
  const path = item?.data[`${type}Picture`];
  if (!item || typeof path !== 'string' || !path) return null;
  return getImage('dms', undefined, path);
};
